import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiLink, FiX, FiLink2 } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const BankStatementLines = () => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [lines, setLines] = useState([]);
  const [bankAccountFilter, setBankAccountFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bankRes, lineRes] = await Promise.all([
        financeAPI.bankAccounts.list(),
        financeAPI.bankStatementLines.list(bankAccountFilter ? { bank_account: bankAccountFilter } : undefined),
      ]);
      setBankAccounts(bankRes.data.results || bankRes.data || []);
      setLines(lineRes.data.results || lineRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load bank statement lines.');
    } finally {
      setLoading(false);
    }
  }, [bankAccountFilter]);

  useEffect(() => { load(); }, [load]);

  const match = async (line) => {
    const journalLineId = window.prompt('Journal line ID to match this statement line to?');
    if (!journalLineId) return;
    setBusyId(line.id);
    setError(null);
    try { await financeAPI.bankStatementLines.match(line.id, journalLineId); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to match — check the journal line ID.'); }
    finally { setBusyId(null); }
  };

  const unmatch = async (line) => {
    setBusyId(line.id);
    setError(null);
    try { await financeAPI.bankStatementLines.unmatch(line.id); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to unmatch.'); }
    finally { setBusyId(null); }
  };

  const bankAccountLabel = (id) => { const b = bankAccounts.find((x) => x.id === id); return b ? `${b.bank_name} — ${b.account_name}` : `#${id}`; };

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiLink size={22} />Bank Statement Lines</h1>
          <p style={fs.pageSubtitle}>Imported bank statement rows. Match each to the journal line it corresponds to — an unmatched line on either side is a discrepancy, never silently dropped.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={bankAccountFilter} onChange={(e) => setBankAccountFilter(e.target.value)} style={{ ...fs.select }}>
            <option value="">All bank accounts</option>
            {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_name}</option>)}
          </select>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : lines.length === 0 ? (
        <div style={fs.emptyState}><FiLink size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No bank statement lines. Import a statement from the Bank Accounts page first.</div></div>
      ) : (
        <div style={fs.tableWrap}>
          <table style={fs.table}>
            <thead><tr style={fs.tableHeadRow}>{['Bank Account', 'Date', 'Description', 'Amount', 'Status', ''].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
            <tbody>
              {lines.map((l, i) => {
                const busy = busyId === l.id;
                return (
                  <tr key={l.id} style={{ borderBottom: i < lines.length - 1 ? '1px solid #111' : 'none' }}>
                    <td style={fs.td}>{bankAccountLabel(l.bank_account)}</td>
                    <td style={fs.td}>{l.line_date}</td>
                    <td style={{ ...fs.td, color: fs.colors.textBright }}>{l.description}</td>
                    <td style={fs.td}>{fs.money(l.amount)}</td>
                    <td style={fs.td}><span style={fs.badge(l.is_matched ? fs.colors.green : fs.colors.amber)}>{l.is_matched ? 'MATCHED' : 'UNMATCHED'}</span></td>
                    <td style={fs.td}>
                      {l.is_matched ? (
                        <button onClick={() => unmatch(l)} disabled={busy} style={fs.dangerButton}>Unmatch</button>
                      ) : (
                        <button onClick={() => match(l)} disabled={busy} style={fs.successButton}><FiLink2 size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Match</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BankStatementLines;
